import { AxiosResponse } from 'axios';
import { createContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { instance } from '../api/db';
import CustomLoading from '../components/uiParts/CustomLoading';
import CustomSnackbar from '../components/uiParts/CustomSnackbar';
import { responseSuccess } from '../constants/Response';
import { userStatus } from '../constants/Enum';
import { sentrySetUser } from '../config/sentry';
import { getCustomerInfo, getCustomerInfoType } from '../api/db/user';

export type LoginStatus = {
  check: boolean;
  id: number | null;
  companyId: number | null;
  status: number | null;
  navi: number | null;
  rank: number | null;
  role: number | null;
};

type AuthContextProps = {
  isLoggedIn: boolean;
  withdraw: () => void;
  logout: () => Promise<void>;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorText: React.Dispatch<React.SetStateAction<string>>;
  setInfoText: React.Dispatch<React.SetStateAction<string>>;
  snackbarOpen: boolean;
  errorText: string;
  userId: number | null;
  companyId: number | null;
  userType: number | null;
  userRank: number | null;
  userRole: number | null;
  checkLoginStatus: () => Promise<LoginStatus>;
  refreshLoginStatus: () => Promise<void>;
  loginStatus: LoginStatus | null;
  addMembersLimit: number | null;
  customerInfo: getCustomerInfoType | null;
};

export const getLoginStatus = async (): Promise<AxiosResponse> => {
  return await instance.get('/users/status');
};

const getCookie = (name: string): string | undefined => {
  // cookieからXSRF-TOKENを取得
  const value: string = `; ${document.cookie}`;
  const parts: string[] = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
};

export const checkLoginStatus = async (): Promise<LoginStatus> => {
  try {
    const response = await getLoginStatus();
    localStorage.setItem('user_type', response.data.user_type);
    if (response.status === responseSuccess) {
      return {
        check: true,
        id: response.data.user_id,
        companyId: response.data.company_id,
        status: response.data.user_status,
        navi: response.data.user_navi_status,
        rank: response.data.user_rank,
        role: response.data.user_role
      };
    }
    return {
      check: false,
      id: null,
      companyId: null,
      status: null,
      navi: null,
      rank: null,
      role: null
    };
  } catch (err) {
    return {
      check: false,
      id: null,
      companyId: null,
      status: null,
      navi: null,
      rank: null,
      role: null
    };
  }
};

const AuthContext = createContext<Partial<AuthContextProps>>({});

const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [infoText, setInfoText] = useState<string>('');
  const [userId, setUserId] = useState<number | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [userRank, setUserRank] = useState<number | null>(null);
  const [userType, setUserType] = useState<number | null>(null);
  const [loginStatus, setLoginStatus] = useState<LoginStatus | null>(null);
  const [customerInfo, setCustomerInfo] = useState<getCustomerInfoType | null>(
    null
  );
  const navigate = useNavigate();

  const fetchData = async () => {
    setIsLoading(true);
    const result = await checkLoginStatus();
    setLoginStatus(result);
    if (
      result.status === userStatus.inactive ||
      result.status === userStatus.withdraw
    ) {
      await logout();
      setIsLoading(false);
      return;
    }

    if (result.check) {
      const userInfo = await getCustomerInfo();
      setUserRank(userInfo.data.rank);
      setUserType(userInfo.data.type);
      setCustomerInfo(userInfo.data);
    }

    setIsLoggedIn(result.check);
    setUserId(result.id);
    setCompanyId(result.companyId);
    setIsLoading(false);

    // Sentryにユーザー情報をセット
    result.id && sentrySetUser(result.id);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const clearCookie = (): void => {
    const cookie = getCookie('XSRF-TOKEN');
    //Cookieを削除する
    const dt = new Date('1999-12-31T23:59:59Z'); //過去の日付を入れる
    document.cookie =
      'XSRF-TOKEN=' + cookie + '; expires=' + dt.toUTCString() + '; path=/;';
  };

  const withdraw = () => {
    try {
      setIsLoggedIn(false);
      clearCookie();
    } catch (e) {
      setErrorText('退会に失敗しました。');
      setSnackbarOpen(true);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      const csrfCookie = document.cookie
        .split('; ')
        .find((cookie) => cookie.startsWith('XSRF-TOKEN='));

      const csrftoken = csrfCookie
        ? decodeURIComponent(csrfCookie.split('=')[1])
        : null;

      const response = await instance.post(
        '/logout',
        {},
        {
          headers: { 'X-XSRF-TOKEN': csrftoken }
        }
      );

      if (response.status === 200) {
        setIsLoggedIn(false);
        clearCookie();
        navigate('/auth/login');
      }
    } catch (e) {
      setErrorText('ログアウトに失敗しました。');
      setSnackbarOpen(true);
    }
  };

  if (isLoading) {
    return <CustomLoading loading={isLoading} />;
  }

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        logout,
        withdraw,
        setSnackbarOpen,
        setErrorText,
        setInfoText,
        snackbarOpen,
        errorText,
        userId,
        companyId,
        userType,
        userRank,
        checkLoginStatus,
        refreshLoginStatus: fetchData,
        loginStatus,
        customerInfo
      }}
    >
      {children}
      <CustomSnackbar
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        open={snackbarOpen}
        text={errorText}
        infoText={infoText}
      />
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
